import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CountdownTime, CountdownConfig } from '@/types';
import './CountdownTimer.css';

/**
 * Default event configuration for Re:Nav-96 30th Year Reunion
 */
const DEFAULT_CONFIG: CountdownConfig = {
  targetDate: '2026-06-13T09:00:00+05:30', // June 13th, 2026 at 9 AM IST
  eventName: '30th Year Reunion',
  showSeconds: true,
};

/**
 * Props for the CountdownTimer component
 */
interface CountdownTimerProps {
  /** Custom configuration to override defaults */
  config?: Partial<CountdownConfig>;
  /** Callback when countdown reaches zero */
  onComplete?: () => void;
  /** Optional CSS class name for container */
  className?: string;
  /** Compact mode for smaller displays */
  compact?: boolean;
}

/**
 * Calculate the time remaining until the target date
 */
function calculateTimeRemaining(targetDate: Date): CountdownTime {
  const now = new Date();
  const difference = targetDate.getTime() - now.getTime();

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      total: 0,
      isExpired: true,
    };
  }

  const seconds = Math.floor((difference / 1000) % 60);
  const minutes = Math.floor((difference / (1000 * 60)) % 60);
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));

  return {
    days,
    hours,
    minutes,
    seconds,
    total: difference,
    isExpired: false,
  };
}

/**
 * Format a number with leading zero for display
 */
function padNumber(num: number): string {
  return num.toString().padStart(2, '0');
}

/**
 * Individual time unit display component
 */
interface TimeUnitProps {
  value: number;
  label: string;
  compact?: boolean;
}

function TimeUnit({ value, label, compact = false }: TimeUnitProps) {
  return (
    <div className={`countdown-unit ${compact ? 'countdown-unit-compact' : ''}`}>
      <div className="countdown-value-wrapper">
        <span className="countdown-value">{padNumber(value)}</span>
      </div>
      <span className="countdown-label">{label}</span>
    </div>
  );
}

/**
 * Separator between time units
 */
function Separator({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`countdown-separator ${compact ? 'text-xl' : 'text-3xl md:text-4xl'}`}>
      <span className="animate-pulse">:</span>
    </div>
  );
}

/**
 * CountdownTimer Component
 *
 * Displays an animated countdown timer to the Re:Nav-96 30th Year Reunion
 * scheduled for June 13th, 2026.
 *
 * Features:
 * - Real-time countdown with days, hours, minutes, seconds
 * - Animated value changes
 * - Responsive design with compact mode option
 * - Customizable target date and event name
 * - Callback when countdown completes
 *
 * @example
 * ```tsx
 * <CountdownTimer />
 *
 * <CountdownTimer
 *   config={{ showSeconds: false }}
 *   compact
 * />
 *
 * <CountdownTimer
 *   onComplete={() => console.log('Event started!')}
 * />
 * ```
 */
export function CountdownTimer({
  config = {},
  onComplete,
  className = '',
  compact = false,
}: CountdownTimerProps) {
  // Merge provided config with defaults
  const mergedConfig: CountdownConfig = { ...DEFAULT_CONFIG, ...config };
  // Memoize the Date by its primitive ISO/timestamp value. Without this,
  // `new Date(...)` produces a fresh object reference every render, which
  // makes `updateCountdown` (useCallback) and its enclosing useEffect
  // re-create every render — `setTimeRemaining({...})` then fires on each
  // render and React's `Object.is` bail-out doesn't kick in (new object
  // each time), driving an infinite render loop.
  const targetDate = useMemo(() => new Date(mergedConfig.targetDate), [mergedConfig.targetDate]);

  // State for countdown time
  const [timeRemaining, setTimeRemaining] = useState<CountdownTime>(() =>
    calculateTimeRemaining(targetDate)
  );

  // Track if onComplete has been called
  const [hasCompletedCallback, setHasCompletedCallback] = useState(false);

  // Update countdown every second
  const updateCountdown = useCallback(() => {
    const newTime = calculateTimeRemaining(targetDate);
    setTimeRemaining(newTime);

    // Call onComplete once when countdown expires
    if (newTime.isExpired && !hasCompletedCallback && onComplete) {
      setHasCompletedCallback(true);
      onComplete();
    }
  }, [targetDate, hasCompletedCallback, onComplete]);

  useEffect(() => {
    // Initial calculation
    updateCountdown();

    // Set up interval to update every second
    const intervalId = setInterval(updateCountdown, 1000);

    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [updateCountdown]);

  // Render expired message when countdown is complete
  if (timeRemaining.isExpired) {
    return (
      <div className={`countdown-container countdown-expired ${className}`}>
        <div className="text-center">
          <h2 className="text-2xl md:text-4xl font-display font-bold text-primary animate-pulse">
            The Reunion is Here!
          </h2>
          <p className="text-white/80 mt-2 text-lg">
            {mergedConfig.eventName} has begun
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`countdown-container ${compact ? 'countdown-compact' : ''} ${className}`}>
      {/* Event name header */}
      {!compact && (
        <div className="text-center mb-6">
          <p className="text-white/60 text-sm uppercase tracking-widest mb-1">
            Countdown to
          </p>
          <h2 className="text-xl md:text-2xl font-display font-semibold text-white">
            {mergedConfig.eventName}
          </h2>
        </div>
      )}

      {/* Countdown display */}
      <div className="countdown-grid">
        <TimeUnit value={timeRemaining.days} label="Days" compact={compact} />
        <Separator compact={compact} />
        <TimeUnit value={timeRemaining.hours} label="Hours" compact={compact} />
        <Separator compact={compact} />
        <TimeUnit value={timeRemaining.minutes} label="Minutes" compact={compact} />
        {mergedConfig.showSeconds && (
          <>
            <Separator compact={compact} />
            <TimeUnit value={timeRemaining.seconds} label="Seconds" compact={compact} />
          </>
        )}
      </div>

      {/* Date display below countdown */}
      {!compact && (
        <div className="text-center mt-6">
          <p className="text-white/60 text-sm">
            June 13th, 2026
          </p>
        </div>
      )}
    </div>
  );
}

export default CountdownTimer;
